import * as tslib_1 from "tslib";
import './carousel.less';
import deepEqual from 'deep-equal';
import { calc, listen, pointer, spring, styler, value } from 'popmotion';
import { debounce } from 'throttle-debounce';
import { m } from '../hyperscript';
import { Lethargy } from '../util/lethargy';
import { View } from './view';
// Todo:
// https://www.w3.org/WAI/tutorials/carousels/structure/
// http://w3c.github.io/aria-practices/examples/carousel/carousel-1/carousel-1.html
// infinite scroll - lazy load - autoplay
var mix = calc.getValueFromProgress;
var closest = function (snaps, value) {
    return snaps.reduce(function (prev, curr) {
        return Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev;
    });
};
var angleIsVertical = function (angle) {
    var isUp = angle <= -90 + 45 && angle >= -90 - 45;
    var isDown = angle <= 90 + 45 && angle >= 90 - 45;
    return isUp || isDown;
};
var Carousel = /** @class */ (function (_super) {
    tslib_1.__extends(Carousel, _super);
    function Carousel() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.offset = value(0);
        _this.snaps = { pages: [0], elements: [0] };
        _this.activePage = 0;
        _this.preventClick = false;
        _this.destination = 0;
        _this.lethargy = new Lethargy();
        _this.wheelStart = null;
        _this.wheelPanning = false;
        _this.onWheel = function (e) {
            if (_this.wheelStart === null)
                _this.wheelStart = _this.x;
            var angle = calc.angle({
                x: e.deltaX,
                y: e.deltaY
            });
            var direction = _this.lethargy.check(e.deltaX);
            if (angleIsVertical(angle))
                return;
            e.stopPropagation();
            e.preventDefault();
            if (direction === false)
                return;
            if (_this.wheelPanning)
                return;
            _this.goTo(_this.activePage + direction);
            _this.wheelPanning = true;
            setTimeout(function () { return (_this.wheelPanning = false); }, 500);
        };
        _this.onResize = debounce(250, function () { return _this.onUpdate(); });
        _this.overDrag = function (v) {
            var _a = _this.attrs.tug, tug = _a === void 0 ? 0.4 : _a;
            var min = -_this.max, max = 0;
            if (v < min)
                return mix(min, v, tug);
            if (v > max)
                return mix(max, v, tug);
            return v;
        };
        _this.snapToAnimation = function (options) {
            spring(tslib_1.__assign({}, options, { velocity: _this.offset.getVelocity(), stiffness: 100, damping: 20 })).start(_this.offset);
            _this.redraw();
        };
        _this.goTo = function (pageIndex) {
            var pages = _this.snaps.pages;
            if (pageIndex < 0)
                return;
            if (pageIndex > pages.length - 1)
                return;
            _this.spring(pages[pageIndex]);
        };
        _this.goToElement = function (elementIndex) {
            var elements = _this.snaps.elements;
            _this.spring(elements[elementIndex]);
        };
        _this.isActive = function (elementIndex) {
            var width = _this.dom.offsetWidth;
            var x = _this.destination;
            var pos = _this.snaps.elements[elementIndex];
            var next = _this.snaps.elements[elementIndex + 1];
            // The 5 here accounts for rounding errors on high dpi screens,
            // there's possibly a better way but I don't see it at this time
            return pos >= x && next - 5 <= x + width;
        };
        return _this;
    }
    Object.defineProperty(Carousel.prototype, "content", {
        get: function () {
            return this.dom.firstChild;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Carousel.prototype, "max", {
        get: function () {
            return this.content.scrollWidth - this.dom.offsetWidth;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Carousel.prototype, "x", {
        get: function () {
            return this.offset.get();
        },
        enumerable: true,
        configurable: true
    });
    Carousel.prototype.onCreate = function () {
        var _this = this;
        this.onUpdate();
        var contentStyler = styler(this.content);
        var clearSubscription = this.offset.subscribe(function (offset) {
            contentStyler.set('x', offset);
        }).unsubscribe;
        var snapToPoint = function (start) {
            var _a = _this.attrs, _b = _a.snapTo, snapTo = _b === void 0 ? 'pages' : _b, _c = _a.power, power = _c === void 0 ? 0.25 : _c;
            var snaps = _this.snaps[snapTo];
            var from = _this.x;
            var velocity = _this.offset.getVelocity();
            var distance = from - start;
            var amplitude = power * velocity;
            var idealTarget = Math.round(from + amplitude);
            var snap = closest(snaps, -idealTarget);
            _this.preventClick = Math.abs(distance) > 1;
            _this.spring(snap);
        };
        var clearTrack = function () { };
        var clearMove = listen(this.dom, 'mousedown touchstart').start(function (event) {
            var start = _this.x;
            if (event instanceof MouseEvent && event.which !== 1)
                return;
            _this.preventClick = false;
            clearTrack = pointer({
                x: start,
                preventDefault: false
            })
                .pipe(function (pos) { return pos.x; }, _this.overDrag)
                .start(_this.offset).stop;
            listen(document, 'mouseup touchend', { once: true }).start(function () {
                return snapToPoint(start);
            });
        }).stop;
        var onClick = function (e) {
            if (!_this.preventClick)
                return clearTrack();
            e.stopPropagation();
            e.preventDefault();
        };
        this.dom.addEventListener('click', onClick, true);
        var clearClick = function () {
            return _this.dom.removeEventListener('click', onClick, true);
        };
        this.dom.addEventListener('wheel', this.onWheel);
        var clearWheel = function () { return _this.dom.removeEventListener('wheel', _this.onWheel); };
        window.addEventListener('resize', this.onResize);
        var clearResize = function () {
            return window.removeEventListener('resize', _this.onResize);
        };
        this.onRemove = function () {
            clearSubscription();
            clearMove();
            clearTrack();
            clearClick();
            clearWheel();
            clearResize();
        };
        this.redraw();
    };
    Carousel.prototype.onUpdate = function () {
        var oldSnaps = tslib_1.__assign({}, this.snaps);
        this.snaps = this.calcSnaps();
        var connect = this.attrs.connect;
        if (connect)
            connect(this);
        if (deepEqual(oldSnaps, this.snaps))
            return;
        this.spring(this.snaps.pages[this.activePage]);
    };
    Carousel.prototype.calcSnaps = function () {
        var pageWidth = this.dom.offsetWidth;
        var children = Array.from(this.content.children);
        return children.reduce(function (_a, child, i) {
            var pages = _a.pages, elements = _a.elements;
            var prevPage = pages[pages.length - 1];
            var prevElement = elements[elements.length - 1];
            var width = child.offsetWidth;
            var offset = prevElement + width;
            elements.push(offset);
            if (prevElement > 0 && offset - prevPage > pageWidth)
                pages.push(prevElement);
            return { pages: pages, elements: elements };
        }, { pages: [0], elements: [0] });
    };
    Carousel.prototype.spring = function (destination, animate) {
        if (animate === void 0) { animate = this.snapToAnimation; }
        var from = this.x;
        this.destination = Math.min(destination, this.max);
        var to = -this.destination;
        this.setActivePage(destination);
        if (from !== to)
            animate({ from: from, to: to });
    };
    Carousel.prototype.setActivePage = function (destination) {
        var pages = this.snaps.pages;
        this.activePage = pages.indexOf(closest(pages, destination));
    };
    Carousel.prototype.preselectPage = function (index) {
        var _this = this;
        var pages = this.snaps.pages;
        var destination = pages[index];
        if (destination > 0)
            this.spring(destination, function (_a) {
                var to = _a.to;
                _this.offset.update(to);
            });
    };
    Carousel.prototype.render = function () {
        var _a = this.attrs, overflow = _a.overflow, unstyled = _a.unstyled, className = _a.className;
        var style = !(overflow || unstyled) ? { overflow: 'hidden' } : {};
        return (m("div", { className: "carousel", style: style, class: className },
            m("div", { class: "carousel-content", onDragStart: function (e) { return e.preventDefault(); } }, this.children)));
    };
    return Carousel;
}(View));
export { Carousel };
